import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;

type TooltipCtx = {
  open: boolean;
  setOpen: (open: boolean) => void;
  openFromTouch: () => void;
};

const TooltipContext = React.createContext<TooltipCtx | null>(null);

const TOUCH_AUTO_DISMISS_MS = 4000;

const Tooltip = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(!!defaultOpen);
  const open = isControlled ? !!openProp : uncontrolledOpen;
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => clearTimer, [clearTimer]);

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!next) clearTimer();
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [clearTimer, isControlled, onOpenChange],
  );

  const openFromTouch = React.useCallback(() => {
    clearTimer();
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    timerRef.current = setTimeout(() => setOpen(false), TOUCH_AUTO_DISMISS_MS);
  }, [clearTimer, open, setOpen]);

  // Close touch-opened tooltips on scroll
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    return () => window.removeEventListener("scroll", close, true);
  }, [open, setOpen]);

  const ctx = React.useMemo(
    () => ({ open, setOpen, openFromTouch }),
    [open, setOpen, openFromTouch],
  );

  return (
    <TooltipContext.Provider value={ctx}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  );
};

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onPointerDown, onPointerUp, onPointerLeave, ...props }, ref) => {
  const ctx = React.useContext(TooltipContext);

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        if (event.defaultPrevented) return;
        if (event.pointerType === "touch" || event.pointerType === "pen") {
          ctx?.openFromTouch();
        }
      }}
      onPointerUp={(event) => {
        onPointerUp?.(event);
        // Radix closes the tooltip on pointerup for touch — keep it open instead
        if (event.pointerType === "touch" || event.pointerType === "pen") {
          event.preventDefault();
        }
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        if (event.pointerType === "touch" || event.pointerType === "pen") {
          event.preventDefault();
        }
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, onPointerDownOutside, onEscapeKeyDown, ...props }, ref) => {
  const ctx = React.useContext(TooltipContext);

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      onPointerDownOutside={(event) => {
        onPointerDownOutside?.(event);
        ctx?.setOpen(false);
      }}
      onEscapeKeyDown={(event) => {
        onEscapeKeyDown?.(event);
        ctx?.setOpen(false);
      }}
      className={cn(
        "z-50 max-w-[80vw] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
